"use client";

import { useDeferredValue, useState } from "react";

import type { TaggedProduct, TagDefinition } from "../../_types/content";
import ProductCard from "./ProductCard";
import CatalogTemplate from "./ui/CatalogTemplate";
import { FilterDropdown, getGroupedTagOptions } from "./ui/GroupedTagFilters";
import { Select } from "./ui/Primitives";

export type ProductSort = "featured" | "name-ascending";

export interface ProductCatalogFilters {
  tagIdsByType: Record<string, string[]>;
  sort: ProductSort;
}

const defaultFilters: ProductCatalogFilters = {
  tagIdsByType: {},
  sort: "featured",
};

export function getProductFilterOptions(
  products: readonly TaggedProduct[],
  tags: readonly TagDefinition[],
) {
  return getGroupedTagOptions(
    new Set(products.flatMap(({ tagIds }) => tagIds)),
    tags,
  );
}

export function filterAndSortProducts(
  products: readonly TaggedProduct[],
  filters: ProductCatalogFilters,
) {
  return [...products]
    .filter((product) =>
      Object.values(filters.tagIdsByType).every(
        (selectedTagIds) =>
          selectedTagIds.length === 0 ||
          selectedTagIds.some((tagId) => product.tagIds.includes(tagId)),
      ),
    )
    .toSorted((first, second) => {
      if (filters.sort === "name-ascending") {
        return first.name.localeCompare(second.name);
      }
      if (first.featured !== second.featured) {
        return first.featured ? -1 : 1;
      }
      return (
        (first.featuredOrder ?? Number.MAX_SAFE_INTEGER) -
        (second.featuredOrder ?? Number.MAX_SAFE_INTEGER)
      );
    });
}

function FilterControls({
  filters,
  tagGroups,
  onToggleTag,
  onSortChange,
  onReset,
}: {
  filters: ProductCatalogFilters;
  tagGroups: ReturnType<typeof getProductFilterOptions>;
  onToggleTag: (type: string, tagId: string) => void;
  onSortChange: (sort: ProductSort) => void;
  onReset: () => void;
}) {
  return (
    <div className="space-y-7">
      {tagGroups.map((group) => (
        <FilterDropdown
          key={group.type}
          label={group.label}
          options={group.options.map((tag) => ({
            value: tag.id,
            label: tag.label,
          }))}
          selectedValues={filters.tagIdsByType[group.type] ?? []}
          onToggle={(tagId) => onToggleTag(group.type, tagId)}
        />
      ))}

      <div className="border-t border-burgundy/20" aria-hidden="true" />

      <label className="block font-[700]">
        Sort by
        <Select
          value={filters.sort}
          onChange={(event) => onSortChange(event.target.value as ProductSort)}
        >
          <option value="featured">Featured</option>
          <option value="name-ascending">Name: A-Z</option>
        </Select>
      </label>

      <button
        type="button"
        onClick={onReset}
        className="border-b-2 border-burgundy font-[700]"
      >
        Reset filters
      </button>
    </div>
  );
}

export default function ProductCatalog({
  products,
  tags,
}: {
  products: readonly TaggedProduct[];
  tags: readonly TagDefinition[];
}) {
  const [filters, setFilters] = useState(defaultFilters);
  const deferredFilters = useDeferredValue(filters);
  const filteredProducts = filterAndSortProducts(products, deferredFilters);
  const tagGroups = getProductFilterOptions(products, tags);

  function toggleTag(type: string, tagId: string) {
    setFilters((current) => {
      const selectedTagIds = current.tagIdsByType[type] ?? [];
      return {
        ...current,
        tagIdsByType: {
          ...current.tagIdsByType,
          [type]: selectedTagIds.includes(tagId)
            ? selectedTagIds.filter((value) => value !== tagId)
            : [...selectedTagIds, tagId],
        },
      };
    });
  }

  return (
    <CatalogTemplate
      filters={
        <FilterControls
          filters={filters}
          tagGroups={tagGroups}
          onToggleTag={toggleTag}
          onSortChange={(sort) => setFilters({ ...filters, sort })}
          onReset={() => setFilters(defaultFilters)}
        />
      }
      count={filteredProducts.length}
      countLabel="products"
      isEmpty={filteredProducts.length === 0}
      emptyMessage="No products match these filters."
      onReset={() => setFilters(defaultFilters)}
    >
      {filteredProducts.map((product) => (
        <ProductCard key={product.id} product={product} />
      ))}
    </CatalogTemplate>
  );
}
